// frontend/src/pages/dashboards/dashboardState.js
// Phase 4 step 14a-3: what the dashboard page holds while someone edits, as
// pure functions. The server's dashboard (GET /semantic/dashboards/{id}) is
// turned into a draft {title, description, tiles}; the grid (gridLayout.js)
// moves the tiles; `toSave` turns the draft back into the PUT body. A tile's
// `key` is the grid's stable id: "t<id>" for a saved tile, "n<counter>" for a
// tile not saved yet (the server mints the id on save).
// Step 14b-2: the draft also holds the filter bar's definitions (`filters`,
// filterState.js), and a chart tile's settings its `filter_bindings`.
import { MAX_TILES, readingOrder } from './gridLayout';
import { MAX_FILTERS, definitionProblem, definitionToSave, definitionsFromServer, sameDefinitions } from './filterState';

export const MAX_TITLE = 120;            // the server's limits (backend/app/dashboards/layout.py); the GET also carries them
export const MAX_DESCRIPTION = 2000;
export const MAX_TEXT = 2000;
export const MAX_TILE_TITLE = 120;
export const TILE_HEADER_PX = 40;        // the title row at the top of a chart tile; the chart gets the rest

export const TYPE_LABELS = {
  bar: 'Bar', stackedBar: 'Stacked bar', horizontalBar: 'Horizontal bar', line: 'Line', area: 'Area',
  doughnut: 'Donut', card: 'KPI', table: 'Table', matrix: 'Matrix',
};

export const typeLabel = (type) => TYPE_LABELS[type] || 'Chart';

let counter = 0;
/** A key for a tile that is not saved yet. */
export const newTileKey = () => { counter += 1; return `n${counter}`; };

/** The id of a saved tile from its key, else null. */
export function savedId(key) {
  const m = /^t(\d+)$/.exec(key || '');
  return m ? Number(m[1]) : null;
}

/** The server's tile -> the grid's tile. */
export function tileFromServer(t) {
  return {
    key: `t${t.id}`, kind: t.kind, x: t.x, y: t.y, w: t.w, h: t.h,
    chartId: t.chart_id ?? null, chart: t.chart || null, text: t.text ?? null,
    settings: t.settings && typeof t.settings === 'object' ? { ...t.settings } : {},
  };
}

/** The server's dashboard -> the page's draft. */
export function draftFromServer(dash) {
  return {
    title: dash.title || '',
    description: dash.description || '',
    tiles: (dash.tiles || []).map(tileFromServer),
    filters: definitionsFromServer(dash.filters),
  };
}

/** What a chart tile needs from the chart it shows, built from a chart the picker lists (GET /charts/) or a tile's summary. */
export function chartSummary(chart) {
  if (!chart) return null;
  const spec = chart.spec && typeof chart.spec === 'object' ? chart.spec : null;
  return {
    id: chart.id, title: chart.title || `Chart ${chart.id}`, chart_type: chart.chart_type || 'bar',
    presentation: chart.presentation || null, dataset_id: chart.dataset_id ?? null,
    dataset_name: chart.dataset_name ?? null,
    has_spec: chart.has_spec !== undefined ? Boolean(chart.has_spec) : spec !== null,
    matrix: chart.matrix !== undefined ? chart.matrix : (spec && spec.matrix) || null,
  };
}

/** A new chart tile (without a place: gridLayout.addTile gives it one). */
export function newChartTile(chart) {
  const summary = chartSummary(chart);
  return { key: newTileKey(), kind: 'chart', chartId: summary.id, chart: summary, text: null, settings: {} };
}

/** A new text tile. */
export function newTextTile(text) {
  return { key: newTileKey(), kind: 'text', chartId: null, chart: null, text, settings: {} };
}

/** The title shown on a chart tile: the tile's own, else the chart's, else a note. */
export function tileTitle(tile) {
  if (tile.kind === 'text') return 'Text';
  if (tile.settings && tile.settings.title) return tile.settings.title;
  return tile.chart ? tile.chart.title : 'Chart no longer available';
}

/** What ChartView needs to draw a tile's chart: { type, presentation, matrix }, or null when the chart is gone. */
export function tileViewProps(tile) {
  const c = tile.chart;
  if (!c) return null;
  const type = TYPE_LABELS[c.chart_type] ? c.chart_type : 'table';
  const matrix = type === 'matrix' ? c.matrix || null : null;
  return { type: type === 'matrix' && !matrix ? 'table' : type, presentation: c.presentation || null, matrix };
}

/** Plain text the server accepts: trimmed, no control characters except newlines and tabs, within the limit. */
export function cleanText(value, limit, { oneLine = false } = {}) {
  let s = String(value ?? '').replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, '');
  if (oneLine) s = s.replace(/[\r\n]+/g, ' ');
  s = s.trim();
  return s.length > limit ? s.slice(0, limit) : s;
}

/** What is wrong with a text tile's text, or null. */
export function textProblem(value) {
  const s = cleanText(value, MAX_TEXT + 1);
  if (!s) return 'Type some text.';
  if (s.length > MAX_TEXT) return `Keep it under ${MAX_TEXT.toLocaleString()} characters.`;
  return null;
}

/** Change one tile's fields (text, settings) by key. */
export function updateTile(tiles, key, patch) {
  const i = tiles.findIndex((t) => t.key === key);
  if (i < 0) return tiles;
  return tiles.map((t, j) => (j === i ? { ...t, ...patch } : t));
}

/** The tile's title override: a cleaned one-line title, or none (back to the chart's own). */
export function withTileTitle(tiles, key, title) {
  const s = cleanText(title, MAX_TILE_TITLE, { oneLine: true });
  const tile = tiles.find((t) => t.key === key);
  if (!tile) return tiles;
  const settings = { ...tile.settings };
  if (s && tile.chart && s !== tile.chart.title) settings.title = s;
  else delete settings.title;
  return updateTile(tiles, key, { settings });
}

/** One tile as the PUT body wants it. */
export function tileToSave(tile) {
  const out = { kind: tile.kind, x: tile.x, y: tile.y, w: tile.w, h: tile.h };
  const id = savedId(tile.key);
  if (id) out.id = id;
  if (tile.kind === 'chart') {
    out.chart_id = tile.chartId ?? null;
  } else {
    out.text = tile.text;
  }
  const settings = {};
  if (tile.settings && tile.settings.title) settings.title = tile.settings.title;
  const bindings = tile.kind === 'chart' && tile.settings && tile.settings.filter_bindings;
  if (bindings && Object.keys(bindings).length) settings.filter_bindings = { ...bindings };
  if (Object.keys(settings).length) out.settings = settings;
  return out;
}

/**
 * The PUT body for a draft, against the dashboard's current revision. Tiles go
 * in reading order, which is their stacking order on a phone. Only what
 * differs from the baseline is sent, so a title-only change never rewrites
 * the tiles (and a stale tile list is never saved by accident).
 */
export function toSave(draft, baseline, revision) {
  const body = { revision };
  if (draft.title !== baseline.title) body.title = draft.title;
  if (draft.description !== baseline.description) body.description = draft.description || null;
  if (!sameTiles(draft.tiles, baseline.tiles)) body.tiles = readingOrder(draft.tiles).map(tileToSave);
  if (!sameDefinitions(draft.filters, baseline.filters)) body.filters = (draft.filters || []).map(definitionToSave);
  return body;
}

/** True when the two tile lists would save the same thing (order does not matter: the save sorts by place). */
export function sameTiles(a, b) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  const byKey = new Map(b.map((t) => [t.key, t]));
  return a.every((t) => {
    const u = byKey.get(t.key);
    return Boolean(u) && t.x === u.x && t.y === u.y && t.w === u.w && t.h === u.h
      && t.kind === u.kind && t.chartId === u.chartId && t.text === u.text
      && (t.settings.title || null) === (u.settings.title || null)
      && sameBindings(t.settings.filter_bindings, u.settings.filter_bindings);
  });
}

function sameBindings(a, b) {
  const x = a || {};
  const y = b || {};
  const keys = Object.keys(x);
  return keys.length === Object.keys(y).length
    && keys.every((k) => Object.prototype.hasOwnProperty.call(y, k) && x[k] === y[k]);
}

/** True when there is something to save. */
export function isDirty(draft, baseline) {
  return draft.title !== baseline.title || (draft.description || '') !== (baseline.description || '')
    || !sameTiles(draft.tiles, baseline.tiles) || !sameDefinitions(draft.filters, baseline.filters);
}

/** What is wrong with the draft before it is sent, or null. */
export function draftProblem(draft) {
  if (!cleanText(draft.title, MAX_TITLE + 1, { oneLine: true })) return 'Give the dashboard a title.';
  if (draft.title.length > MAX_TITLE) return `The title must be at most ${MAX_TITLE} characters.`;
  if ((draft.description || '').length > MAX_DESCRIPTION) return `The description must be at most ${MAX_DESCRIPTION.toLocaleString()} characters.`;
  if (draft.tiles.length > MAX_TILES) return `A dashboard holds at most ${MAX_TILES} tiles.`;
  const empty = draft.tiles.find((t) => t.kind === 'chart' && t.chartId == null && !savedId(t.key));
  if (empty) return 'A new tile needs a chart.';
  const filters = draft.filters || [];
  if (filters.length > MAX_FILTERS) return `A dashboard holds at most ${MAX_FILTERS} filters.`;
  for (const f of filters) {
    const p = definitionProblem(f, filters);
    if (p) return `Filter "${f.label || 'untitled'}": ${p}`;
  }
  return null;
}

/** The sentence shown when a save fails. */
export function saveFailureMessage(err) {
  const code = err && err.code;
  if (code === 'DASHBOARD_CHANGED') return 'Someone else saved this dashboard after you opened it.';
  if (code === 'CHART_NOT_FOUND') return 'A chart on this dashboard could not be found. Remove its tile and save again.';
  if (code === 'INVALID_DASHBOARD') return err.message ? `This layout can't be saved: ${err.message}.` : "This layout can't be saved.";
  // 14d: a member the dashboard was shared with view only. The page hides Edit for them; this covers a right taken away
  // while the page was open.
  if (code === 'NO_EDIT_ACCESS') return 'You can open this dashboard but not change it. Ask whoever shared it to give you edit access.';
  if (code === 'FORBIDDEN' || (err && err.status === 403)) return "You don't have permission to change dashboards.";
  if (code === 'NOT_FOUND' || (err && err.status === 404)) return 'This dashboard no longer exists.';
  if (code === 'UNAUTHENTICATED') return 'Your session has ended. Sign in again.';
  return "The dashboard couldn't be saved. Try again.";
}

/** The sentence shown when a dashboard cannot be loaded. */
export function loadFailureMessage(err) {
  const code = err && err.code;
  if (code === 'NOT_FOUND' || (err && err.status === 404)) return 'This dashboard could not be found.';
  if (code === 'UNAUTHENTICATED') return 'Your session has ended. Sign in again.';
  return "The dashboard couldn't be loaded. Try again.";
}

/** "3 tiles", "1 tile", "No tiles yet". */
export function tileCountText(n) {
  if (!n) return 'No tiles yet';
  return `${n} tile${n === 1 ? '' : 's'}`;
}

/** "Updated 5 minutes ago by someone@…", from ISO strings. */
export function updatedText(iso, by, now = Date.now()) {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.round((now - then) / 1000));
  let when;
  if (s < 60) when = 'just now';
  else if (s < 3600) when = `${Math.floor(s / 60)} min ago`;
  else if (s < 86400) { const h = Math.floor(s / 3600); when = `${h} hour${h === 1 ? '' : 's'} ago`; }
  else if (s < 86400 * 30) { const d = Math.floor(s / 86400); when = `${d} day${d === 1 ? '' : 's'} ago`; }
  else when = new Date(then).toLocaleDateString();
  return by ? `Updated ${when} by ${by}` : `Updated ${when}`;
}

/** The charts the picker offers: made with the builder (older charts have no spec and cannot draw on a dashboard), matching the search. */
export function pickableCharts(charts, query) {
  const q = (query || '').trim().toLowerCase();
  return (charts || [])
    .filter((c) => c && c.spec && typeof c.spec === 'object')
    .filter((c) => !q || (c.title || '').toLowerCase().includes(q))
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
}

/** How many of the charts the picker leaves out because they are from the previous builder. */
export const legacyCount = (charts) => (charts || []).filter((c) => c && !(c.spec && typeof c.spec === 'object')).length;
